import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../components/AuthContext';
import Swal from 'sweetalert2';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { 
  FaCalendarAlt, 
  FaMapMarkerAlt, 
  FaFileExport, 
  FaSearch,
  FaDownload,
  FaEraser,
  FaFileInvoice,
  FaUser,
  FaBuilding,
  FaUsers,
  FaFilter,
  FaSync,
  FaTags,
  FaArrowLeft,
  FaArrowRight,
  FaRedo,
  FaExclamationTriangle
} from 'react-icons/fa';
import { BsFiletypeCsv, BsFileEarmarkExcel } from 'react-icons/bs';

export default function DeliveryOrderReport() {
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    status: '',
    employeeId: null
  });
  
  // Reset order state
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [selectedOrderToReset, setSelectedOrderToReset] = useState(null);
  const [resetReason, setResetReason] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  // Options for react-select
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [statusOptions] = useState([
    { value: '', label: 'All Statuses' },
    { value: 'Pending', label: 'Pending' },
    { value: 'Cancelled', label: 'Cancelled' },
    { value: 'Completed', label: 'Completed' }
  ]);

  // Get company ID from user context
  const getCompanyId = () => {
    return user?.user?.company._id || user?.user?.company;
  };

  useEffect(() => {
    // Set default date range as current month (from 1st day of current month to today)
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

    setFilters({
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      status: '',
      employeeId: null
    });
    
    fetchEmployeeOptions();
  }, []);

  useEffect(() => {
    // Validate we have all required filter fields before fetching
    if (filters.startDate && filters.endDate) {
      const startDate = new Date(filters.startDate);
      const endDate = new Date(filters.endDate);
      
      // Additional validation in case direct state manipulation happens
      if (startDate > endDate) {
        console.warn('Start date is after end date - skipping data fetch');
        return;
      }
      
      // Only fetch if we have valid dates
      if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
        fetchDeliveryOrdersData();
      } else {
        console.warn('Invalid date format detected - skipping data fetch');
      }
    }
  }, [page, limit, filters.startDate, filters.endDate, filters.status, filters.employeeId]);

  const fetchEmployeeOptions = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) {
        console.error('No company ID available');
        return;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/employees/company2/${companyId}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Employee data fetched:', data.length, 'employees');
        const options = data.map(emp => ({
          value: emp._id,
          label: `${emp.name} (${emp.id || 'No ID'})`
        }));

        setEmployeeOptions([{ value: '', label: 'All Employees' }, ...options]);
      } else {
        console.error('Failed to fetch employees:', response.status);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchDeliveryOrdersData = async () => {
    // Pre-fetch validation
    if (!filters.startDate || !filters.endDate) {
      Swal.fire({
        icon: 'error',
        title: 'Missing Required Fields',
        text: 'Please select both start and end dates to continue',
      });
      return;
    }
    
    // Validate date range
    const startDate = new Date(filters.startDate);
    const endDate = new Date(filters.endDate);
    
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Dates',
        text: 'Please ensure both dates are valid',
      });
      return;
    }
    
    if (startDate > endDate) {
      Swal.fire({
        icon: 'error',
        title: 'Invalid Date Range',
        text: 'Start date cannot be after end date',
      });
      return;
    }
    
    // Date range validation - warn if range is too wide
    const dayDiff = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (dayDiff > 90) {
      const proceed = await Swal.fire({
        icon: 'warning',
        title: 'Large Date Range',
        text: `You've selected a ${dayDiff} day range. This may take longer to load. Continue?`,
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Adjust Range'
      });
      
      if (!proceed.isConfirmed) {
        return;
      }
    }
    
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        page,
        limit,
        startDate: filters.startDate, // Always include dates - required
        endDate: filters.endDate,
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId })
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('Delivery Orders Response:', data);
        
        if (data.data && data.data.length === 0) {
          // We got a valid response but with no data
          Swal.fire({
            icon: 'info',
            title: 'No Data Found',
            text: 'No delivery orders found with the current filter criteria. Try adjusting your filters.'
          });
        }
        
        setReportData(data);
      } else {
        const errorText = await response.text();
        console.error('Response error:', response.status, errorText);
        
        // Handle specific error cases
        if (response.status === 400) {
          Swal.fire({
            icon: 'error',
            title: 'Invalid Request',
            text: 'Please check your filter values and try again.'
          });
        } else if (response.status === 401 || response.status === 403) {
          Swal.fire({
            icon: 'error',
            title: 'Authentication Error',
            text: 'You do not have permission to view this report. Please log in again or contact an administrator.'
          });
        } else {
          throw new Error(`Failed to fetch delivery orders data: ${response.status}`);
        }
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire('Error', `Failed to fetch delivery orders report data: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    let newFilters = { ...filters, [field]: value };
    
    // Reset to first page when filters change except date changes
    if (field !== 'startDate' && field !== 'endDate') {
      setPage(1);
    }
    
    // Special handling for date fields to ensure valid ranges
    if (field === 'startDate' || field === 'endDate') {
      const startDate = field === 'startDate' ? value : filters.startDate;
      const endDate = field === 'endDate' ? value : filters.endDate;
      
      // Validate date ranges if both are set
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);
        
        // Prevent future dates
        const now = new Date();
        now.setHours(23, 59, 59, 999);

        // If end date is in the future, cap it to today
        if (end > now) {
          Swal.fire({
            icon: 'info',
            title: 'Date Adjusted',
            text: 'End date cannot be in the future. Set to today.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
          newFilters.endDate = now.toISOString().split('T')[0];
        }
        
        // If start date is after end date, adjust the start date
        if (start > end) {
          Swal.fire({
            icon: 'warning',
            title: 'Invalid Date Range',
            text: 'Start date cannot be after end date. Start date adjusted.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000
          });
          newFilters.startDate = newFilters.endDate;
        }
      }
    }
    
    setFilters(newFilters);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const resetFilters = () => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    const newFilters = {
      startDate: startOfMonth.toISOString().split('T')[0],
      endDate: now.toISOString().split('T')[0],
      status: '',
      employeeId: null
    };
    
    setFilters(newFilters);
    setPage(1);
    
    // Fetch data with the reset filters immediately
    setTimeout(() => {
      fetchDeliveryOrdersData();
    }, 0);
    
    Swal.fire({
      position: 'top-end',
      icon: 'success',
      title: 'Filters have been reset',
      text: `Date range set to ${formatDate(startOfMonth)} - ${formatDate(now)}`,
      showConfirmButton: false,
      timer: 2000
    });
  };

  const exportToCSV = async () => {
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        format: 'csv'
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders/export?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `Delivery_Orders_Report_${filters.startDate}_to_${filters.endDate}.csv`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Success', 'CSV file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download CSV file');
      }
    } catch (error) {
      console.error('Error downloading CSV:', error);
      Swal.fire('Error', 'Failed to download CSV file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    setLoading(true);
    try {
      const companyId = getCompanyId();
      const params = new URLSearchParams({
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        ...(filters.status && { status: filters.status }),
        ...(filters.employeeId && { employeeId: filters.employeeId.value }),
        ...(companyId && { companyId }),
        format: 'excel'
      });

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/reports/delivery-orders/export?${params}`,
        {
          credentials: 'include'
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `Delivery_Orders_Report_${filters.startDate}_to_${filters.endDate}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        Swal.fire('Success', 'Excel file downloaded successfully', 'success');
      } else {
        throw new Error('Failed to download Excel file');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      Swal.fire('Error', 'Failed to download Excel file', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    let badgeClass = 'px-2 py-1 rounded text-xs font-medium';
    
    switch (status?.toLowerCase()) {
      case 'pending':
        badgeClass += ' bg-yellow-100 text-yellow-800';
        break;
      case 'cancelled':
        badgeClass += ' bg-red-100 text-red-800';
        break;
      case 'completed':
        badgeClass += ' bg-green-100 text-green-800';
        break;
      default:
        badgeClass += ' bg-gray-100 text-gray-800';
        break;
    }
    
    return <span className={badgeClass}>{status || 'Unknown'}</span>;
  };

  const openResetModal = (order) => {
    setSelectedOrderToReset(order);
    setResetReason('');
    setIsResetModalOpen(true);
  };

  const closeResetModal = () => {
    setIsResetModalOpen(false);
    setSelectedOrderToReset(null);
    setResetReason('');
  };

  const resetDeliveryOrder = async () => {
    if (!selectedOrderToReset || !resetReason.trim()) {
      Swal.fire('Error', 'Please provide a reason for resetting the delivery order', 'error');
      return;
    }

    setResetLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/delivery-order-reset/${selectedOrderToReset._id}/reset`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ 
            resetReason,
            resetBy: user?.user?._id, // Ensure we send the current user ID
            resetByModel: 'Employee' // Default to Employee model since we're in company-head view
          }),
          credentials: 'include'
        }
      );

      if (response.ok) {
        const result = await response.json();
        closeResetModal();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'Delivery order has been reset successfully',
          text: 'The order has been marked as reset and can be identified in reports.',
          showConfirmButton: true
        });
        
        // Refresh data
        fetchDeliveryOrdersData();
      } else {
        const errorData = await response.json();
        if (response.status === 401) {
          Swal.fire('Authentication Error', 'You are not authorized to reset delivery orders.', 'error');
        } else if (response.status === 403) {
          Swal.fire('Permission Denied', 'You do not have permission to reset this delivery order.', 'error');
        } else {
          throw new Error(errorData.message || 'Failed to reset delivery order');
        }
      }
    } catch (error) {
      console.error('Error resetting delivery order:', error);
      Swal.fire({
        icon: 'error',
        title: 'Reset Failed',
        text: error.message || 'Failed to reset delivery order. Please try again.'
      });
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-2 flex items-center">
          <FaFileInvoice className="mr-2" /> Delivery Orders Report
        </h1>
        <p className="text-gray-600">
          View and analyze delivery orders, track performance, and export data.
        </p>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium flex items-center">
            <FaFilter className="mr-2 text-gray-600" /> Report Filters
          </h2>
          <button 
            className="flex items-center px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-gray-700 transition-colors"
            onClick={resetFilters}
          >
            <FaEraser className="mr-2" /> Reset
          </button>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date From Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaCalendarAlt className="inline mr-1" /> Start Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className={`w-full p-2 border ${!filters.startDate ? 'border-red-300' : 'border-gray-300'} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              max={new Date().toISOString().split('T')[0]} // Prevent future dates
              required
            />
            {!filters.startDate && (
              <p className="mt-1 text-xs text-red-500">Start date is required</p>
            )}
          </div>
          
          {/* Date To Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaCalendarAlt className="inline mr-1" /> End Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              className={`w-full p-2 border ${!filters.endDate ? 'border-red-300' : filters.startDate > filters.endDate ? 'border-orange-300' : 'border-gray-300'} rounded focus:outline-none focus:ring-2 focus:ring-blue-500`}
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              min={filters.startDate} // Prevent end date before start date
              max={new Date().toISOString().split('T')[0]} // Prevent future dates
              required
            />
            {!filters.endDate && (
              <p className="mt-1 text-xs text-red-500">End date is required</p>
            )}
            {filters.startDate && filters.endDate && new Date(filters.startDate) > new Date(filters.endDate) && (
              <p className="mt-1 text-xs text-orange-500">End date must be after start date</p>
            )}
          </div>
          
          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaTags className="inline mr-1" /> Status
            </label>
            <Select
              placeholder="Select Status..."
              value={statusOptions.find(option => option.value === filters.status)}
              options={statusOptions}
              onChange={(selected) => handleFilterChange('status', selected.value)}
              className="react-select-container"
              classNamePrefix="react-select"
            />
          </div>
          
          {/* Employee Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FaUser className="inline mr-1" /> Employee
            </label>
            <Select
              placeholder="Select Employee..."
              value={filters.employeeId}
              options={employeeOptions}
              onChange={(selected) => handleFilterChange('employeeId', selected)}
              className="react-select-container"
              classNamePrefix="react-select"
              isClearable
            />
          </div>
        </div>
        
        <div className="mt-4 flex gap-2 justify-between">
          <div className="text-sm text-gray-500 italic">
            {filters.startDate && filters.endDate ? 
              `Showing data from ${formatDate(filters.startDate)} to ${formatDate(filters.endDate)}` : 
              'Select date range to view report data'}
            {filters.status && ` • Status: ${filters.status}`}
            {filters.employeeId && ` • Employee: ${filters.employeeId.label}`}
          </div>
          
          <button
            className="flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:bg-blue-300"
            onClick={() => {
              // Additional validation before search
              if (!filters.startDate || !filters.endDate) {
                Swal.fire({
                  icon: 'error',
                  title: 'Missing Required Fields',
                  text: 'Please select both start and end dates to continue',
                });
                return;
              }
              
              if (new Date(filters.startDate) > new Date(filters.endDate)) {
                Swal.fire({
                  icon: 'error',
                  title: 'Invalid Date Range',
                  text: 'Start date cannot be after end date',
                });
                return;
              }
              
              fetchDeliveryOrdersData();
            }}
            disabled={loading || !filters.startDate || !filters.endDate}
            title={!filters.startDate || !filters.endDate ? "Start and End dates are required" : "Search with current filters"}
          >
            {loading ? (
              <><FaSync className="animate-spin mr-2" /> Loading...</>
            ) : (
              <><FaSearch className="mr-2" /> Search</>
            )}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {reportData && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Orders</p>
                <h3 className="text-2xl font-bold mt-1">{reportData.summary.totalCount}</h3>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <FaFileInvoice className="text-blue-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Total Amount</p>
                <h3 className="text-2xl font-bold mt-1">{formatCurrency(reportData.summary.totalAmount)}</h3>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <FaTags className="text-green-600 text-xl" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 font-medium">Average Order Value</p>
                <h3 className="text-2xl font-bold mt-1">{formatCurrency(reportData.summary.avgAmount)}</h3>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <FaBuilding className="text-purple-600 text-xl" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Export Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          className="flex items-center px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors disabled:bg-green-300"
          onClick={exportToExcel}
          disabled={loading || !reportData}
        >
          <BsFileEarmarkExcel className="mr-2" /> Export to Excel
        </button>
        <button
          className="flex items-center px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded transition-colors disabled:bg-indigo-300"
          onClick={exportToCSV}
          disabled={loading || !reportData}
        >
          <BsFiletypeCsv className="mr-2" /> Export to CSV
        </button>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <h2 className="font-semibold text-gray-800">Delivery Orders List</h2>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
            <p className="mt-4 text-gray-600">Loading delivery orders data...</p>
          </div>
        ) : reportData && reportData.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DO #</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Recipient</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created By</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Salesman</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {reportData.data.map((order) => (
                    <tr key={order._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-medium text-gray-900">{order.doNumber || order._id.substring(0, 8)}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">{formatDate(order.createdAt)}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{order.recipientName}</td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {order.createdByName || order.createdBy?.name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {order.salesmanNames || 
                         (order.salesman?.length > 0 ? order.salesman.map(s => s.name).join(', ') : '—')}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900 font-medium">
                        {formatCurrency(order.totalAmountWithGST || 0)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {getStatusBadge(order.status)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex space-x-2 justify-center">
                          {!order.reset?.isReset && (order.status === 'Completed' || order.status === 'Cancelled') && (
                            <button
                              onClick={() => openResetModal(order)}
                              className="px-2.5 py-1.5 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-md flex items-center text-xs font-medium transition-all shadow-sm border border-indigo-200"
                              title={`Reset Delivery Order #${order.doNumber || order._id.substring(0, 8)}`}
                            >
                              <FaRedo className="mr-1.5" /> Reset
                            </button>
                          )}
                          {order.reset?.isReset && (
                            <span 
                              className="px-2.5 py-1.5 bg-amber-50 text-amber-700 rounded-md flex items-center text-xs font-medium border border-amber-200 cursor-help" 
                              title={`Reset on ${formatDate(order.reset.resetDate)} by ${order.reset.resetBy?.name || 'Unknown'}\nReason: ${order.reset.resetReason || 'No reason provided'}`}
                            >
                              <FaExclamationTriangle className="mr-1.5" /> Reset
                            </span>
                          )}
                          {order.status !== 'Completed' && order.status !== 'Cancelled' && !order.reset?.isReset && (
                            <span className="px-2.5 py-1.5 bg-gray-100 text-gray-500 rounded-md flex items-center text-xs font-medium border border-gray-200 cursor-not-allowed" title="Only completed or cancelled orders can be reset">
                              <FaRedo className="mr-1.5 opacity-50" /> N/A
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {reportData.totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => handlePageChange(page - 1)}
                    disabled={page === 1}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      page === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(page + 1)}
                    disabled={page === reportData.totalPages}
                    className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
                      page === reportData.totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((page - 1) * limit) + 1}</span> to{' '}
                      <span className="font-medium">
                        {Math.min(page * limit, reportData.totalRecords)}
                      </span>{' '}
                      of <span className="font-medium">{reportData.totalRecords}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                        className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                          page === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Previous</span>
                        <FaArrowLeft className="h-5 w-5" aria-hidden="true" />
                      </button>
                      
                      {/* Page numbers */}
                      {Array.from({ length: Math.min(5, reportData.totalPages) }, (_, i) => {
                        // Show pages around current page
                        let pageToShow;
                        if (reportData.totalPages <= 5) {
                          pageToShow = i + 1;
                        } else if (page <= 3) {
                          pageToShow = i + 1;
                          if (i === 4) pageToShow = reportData.totalPages;
                        } else if (page >= reportData.totalPages - 2) {
                          pageToShow = reportData.totalPages - 4 + i;
                        } else {
                          pageToShow = page - 2 + i;
                        }
                        
                        return (
                          <button
                            key={pageToShow}
                            onClick={() => handlePageChange(pageToShow)}
                            className={`relative inline-flex items-center px-4 py-2 border ${
                              page === pageToShow
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            } text-sm font-medium`}
                          >
                            {pageToShow}
                          </button>
                        );
                      })}
                      
                      <button
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === reportData.totalPages}
                        className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                          page === reportData.totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        <span className="sr-only">Next</span>
                        <FaArrowRight className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-8 text-center">
            <p className="text-gray-500">No delivery orders found with the selected filters.</p>
            <button 
              onClick={resetFilters}
              className="mt-4 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FaSync className="mr-2" /> Reset Filters
            </button>
          </div>
        )}
      </div>

      {/* Reset Order Modal */}
      {isResetModalOpen && selectedOrderToReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg animate-fadeIn">
            <div className="p-4 border-b flex justify-between items-center bg-indigo-50">
              <h3 className="text-lg font-semibold text-indigo-800 flex items-center">
                <FaRedo className="mr-2 text-indigo-600" /> Reset Delivery Order
              </h3>
              <button 
                onClick={closeResetModal}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-6 rounded-r">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <FaExclamationTriangle className="h-5 w-5 text-amber-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-amber-700 font-medium">Caution</p>
                    <p className="text-sm text-amber-600 mt-1">
                      This action will mark the order as reset and cannot be undone. The order will be flagged in reports.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-white border border-gray-200 rounded-md p-4 mb-6 shadow-sm">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">DO Number</p>
                    <p className="text-sm font-semibold mt-1">{selectedOrderToReset?.doNumber || selectedOrderToReset?._id?.substring(0, 8)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Date Created</p>
                    <p className="text-sm font-semibold mt-1">{formatDate(selectedOrderToReset?.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Amount</p>
                    <p className="text-sm font-semibold mt-1 text-indigo-600">{formatCurrency(selectedOrderToReset?.totalAmountWithGST || 0)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Recipient</p>
                    <p className="text-sm font-semibold mt-1">{selectedOrderToReset?.recipientName}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Created By</p>
                    <p className="text-sm font-semibold mt-1">{selectedOrderToReset?.createdByName || 'Unknown'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 uppercase font-medium">Status</p>
                    <div className="mt-1">{getStatusBadge(selectedOrderToReset.status)}</div>
                  </div>
                </div>
              </div>
              
              <form onSubmit={(e) => {
                e.preventDefault();
                if (resetReason.trim()) {
                  resetDeliveryOrder();
                }
              }}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Reason for Reset <span className="text-red-500 ml-1">*</span>
                  </label>
                  <textarea
                    className={`w-full p-3 border ${!resetReason.trim() ? 'border-red-300 bg-red-50' : 'border-gray-300'} rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 transition-all`}
                    rows="4"
                    value={resetReason}
                    onChange={(e) => setResetReason(e.target.value)}
                    placeholder="Please provide a detailed reason for resetting this delivery order..."
                    required
                  ></textarea>
                  {!resetReason.trim() && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      Reason is required for audit purposes
                    </p>
                  )}
                  <p className="mt-2 text-xs text-gray-500">
                    This information will be stored with the order history and visible in reports.
                  </p>
                </div>
              
                <div className="flex justify-end pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeResetModal}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 mr-3 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`px-4 py-2 rounded-md shadow-sm flex items-center ${!resetReason.trim() || resetLoading
                      ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                      : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
                    disabled={resetLoading || !resetReason.trim()}
                  >
                    {resetLoading ? (
                      <>
                        <FaSync className="animate-spin mr-2" /> Resetting...
                      </>
                    ) : (
                      <>
                        <FaExclamationTriangle className="mr-2" /> Confirm Reset
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
